<template>
    <div class="step">
        <!-- User selection - only show when multiple users -->
        <div v-if="rewards.users.length > 1">
            <UiInput
                id="user-select"
                label="User"
                :options="{ hideLabel: false }"
            >
                <template #input>
                    <div class="relative mb-6">
                        <div
                            class="relative w-full"
                            v-click-outside="closeUserDropdown"
                        >
                            <button
                                type="button"
                                ref="userDropdownBtn"
                                class="form-select w-full py-2.5 pl-3 pr-10 appearance-none border rounded-md bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-sm cursor-pointer flex items-center transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-800 dark:focus:border-slate-300 hover:border-slate-800 dark:hover:border-slate-300"
                                :class="{
                                    'border-slate-200 dark:border-slate-600': !hasUserError,
                                    'border-red-500 border': hasUserError,
                                }"
                                @click="$emit('toggle-user-dropdown')"
                                @keydown.enter.prevent="$emit('toggle-user-dropdown')"
                                @keydown.space.prevent="$emit('toggle-user-dropdown')"
                                @keydown.escape="$emit('close-user-dropdown')"
                            >
                                <div class="flex-shrink-0 mr-2">
                                    <IconProgram
                                        icon="people"
                                        class="w-5 h-5 text-slate-800 dark:text-slate-300"
                                    />
                                </div>
                                <span
                                    v-if="program.user && program.user.name"
                                    class="truncate text-sm font-normal font-['Inter'] leading-5"
                                >
                                    {{ program.user.name }}
                                </span>
                                <span
                                    v-else
                                    class="text-slate-500 dark:text-slate-400 truncate text-sm font-normal font-['Inter'] leading-5"
                                >
                                    Select User
                                </span>
                            </button>

                            <teleport to="body">
                                <div
                                    v-if="showUserDropdown"
                                    class="fixed border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 shadow-md z-[9999]"
                                    style="
                                        border-radius: 8px;
                                        box-shadow:
                                            0px 1px 3px 0px rgba(0, 0, 0, 0.1),
                                            0px 1px 2px 0px rgba(0, 0, 0, 0.06);
                                        max-height: 250px;
                                        overflow-y: auto;
                                    "
                                    :style="{
                                        top: dropdownTop + 'px',
                                        left: dropdownLeft + 'px',
                                        width: dropdownWidth + 'px',
                                    }"
                                >
                                    <div class="py-1" role="listbox">
                                        <button
                                            type="button"
                                            v-for="(user, i) in rewards.users"
                                            :key="i"
                                            class="dropdown-option py-2.5 pl-3 pr-4 cursor-pointer block whitespace-nowrap w-full text-left text-sm font-medium transition-colors focus:outline-none focus:bg-slate-100 dark:focus:bg-slate-700"
                                            :class="{
                                                'text-slate-800 dark:text-slate-100 bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700':
                                                    user.id === program.user?.id,
                                                'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700':
                                                    user.id !== program.user?.id,
                                            }"
                                            role="option"
                                            :aria-selected="user.id === program.user?.id"
                                            @click="$emit('select-user', user)"
                                            @keydown.enter.prevent="$emit('select-user', user)"
                                            @keydown.escape="$emit('close-user-dropdown')"
                                        >
                                            <div class="flex items-center">
                                                <div class="flex-shrink-0 mr-2">
                                                    <IconProgram
                                                        icon="people"
                                                        class="w-5 h-5 text-slate-800 dark:text-slate-300"
                                                    />
                                                </div>
                                                <span>{{ user.name }}</span>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            </teleport>
                        </div>
                    </div>
                </template>
            </UiInput>
        </div>
        <!-- Program Type Selection -->
        <div v-if="rewards.users.length <= 1 || program.user">
            <h4
                class="text-sm text-slate-600 dark:text-slate-300 font-medium font-['Inter'] leading-5 mb-3"
            >
                Program type
            </h4>
            <div class="flex flex-wrap gap-4">
                <button
                    type="button"
                    v-for="(programType, typeIndex) in filteredProgramTypes"
                    :key="'program-type-' + typeIndex"
                    class="p-3 rounded-lg border border-solid cursor-pointer relative flex flex-col items-start gap-2 flex-1 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300 text-left"
                    :class="{
                        'border-orange border-2 bg-orange/5':
                            programType.type === program.type,
                        'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50':
                            programType.type !== program.type,
                    }"
                    @click="$emit('update:program-type', programType.type)"
                    role="radio"
                    :aria-checked="programType.type === program.type"
                >
                    <div
                        class="w-5 h-5 rounded-full absolute top-2 right-2 flex items-center justify-center"
                        :class="{
                            'border-orange border-[6px] bg-white dark:bg-slate-800':
                                programType.type === program.type,
                            'border-slate-300 dark:border-slate-600 border bg-white dark:bg-slate-800':
                                programType.type !== program.type,
                        }"
                        style="box-sizing: border-box"
                    ></div>
                    <IconProgram
                        :icon="programType.icon"
                        class="w-5 h-5 text-slate-500 dark:text-slate-400 ml-0"
                    />
                    <span
                        class="text-sm font-medium text-slate-800 dark:text-slate-100 font-['Inter'] leading-5"
                    >
                        {{ programType.label }}
                    </span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import IconProgram from '@/components/ui/icon/icon-program.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import { vClickOutside } from 'click-outside-vue3';

const props = defineProps({
    program: { type: Object, required: true },
    rewards: { type: Object, required: true },
    errors: { type: Array, required: true },
    filteredProgramTypes: { type: Array, required: true },
    showUserDropdown: { type: Boolean, required: true },
    dropdownTop: { type: Number, required: true },
    dropdownLeft: { type: Number, required: true },
    dropdownWidth: { type: Number, required: true },
});

const emit = defineEmits(['toggle-user-dropdown', 'close-user-dropdown', 'select-user', 'update:program-type']);

function closeUserDropdown() {
    emit('close-user-dropdown');
}

const userDropdownBtn = ref(null);

defineExpose({ userDropdownBtn });

const hasUserError = computed(() => {
    return props.errors.some((error) => error.message === 'Please choose a user.');
});
</script>
